/**
 * 按「录入时的那一行」分组（2026-10-20）。
 *
 * ## 用户原话（验收标准）
 *
 * > "在『输入』这边，粘贴字表之后点『预览』，下面会列出所有的字。我对这些字的排列
 * >  有个小要求：**能否按照我每一行录入的字来排列**，按行来排列那些字？你现在是
 * >  **连续排列**，这样连续排列的情况下，**我就不知道录入时这个字属于第几行了**。
 * >  正确的排列方式是：① **一行的字就顺着排**；② **第二行就换行**，就按照我录入时
 * >  的行来排列。这样我核对的时候，可以**一行一行地核对**。"
 *
 * 同一句话后来也用在了「浏览与编辑」的单表视图上（"一行一行地核对"），所以这里的
 * 分组逻辑**只有一份**：入参从 `ListingPositionInfo[]` 泛化成任何
 * `{ line, indexInLine, listType? }`（`LineGroupedItem`），导入预览与浏览页共用。
 * ⛔ 不要为了另一边再抄一份 —— 两份实现迟早漂移。
 *
 * ## 分组口径（三句话）
 *
 *   1. **一行 = 一个分组**（`line` 相同的字放一块）；
 *   2. **分组之间按行号升序**；**组内按 `indexInLine` 升序** —— 就是粘贴顺序，
 *      与写入时的 `sort_order` 同口径；
 *   3. 同一行可能出现在**两张字表**上（一次粘贴里既有「识字表」又有「写字表」的行），
 *      所以分组键是 `listType|line`，不单是 `line`；混表时标题带上表名
 *      （`识字表 · 第 3 行（7 个字）`），单表时只写 `第 3 行（7 个字）`。
 *
 * ⚠️ 这里**只做分组与标题文案**（纯函数、好测）；渲染与跳转接线在
 *    `ImportListingPreview.tsx`（导入预览）/ `DictationItemsPanel.tsx`（浏览与编辑）。
 */
import type { HanziListType, ListingPositionInfo } from '@fht/shared';
import { listTypeShortLabel } from '../../lib/api/dictation';

/**
 * 分组只要求这三样（**入参的泛化形状**）。
 *
 * `listType` 可选：浏览页的一组字卡天然只属于一张表（筛选条件里已经定了），
 * 没有这个字段时就按"不编表名"处理（与老后端没给 `listType` 时同一条路）。
 */
export interface LineGroupedItem {
  /** 原文行号（1 开始）。 */
  line: number;
  /** **这一行的第几个字/词**（1 开始，与 `sort_order` 同口径）。 */
  indexInLine: number;
  /** 落在哪张字表上；缺省 = 不知道（标题不编表名）。 */
  listType?: HanziListType | null;
}

/** 一个「行分组」。 */
export interface LineGroup<T extends LineGroupedItem = ListingPositionInfo> {
  /** React key：`listType|line`（同号不同表也是两组）。 */
  key: string;
  /** 这一组落在哪张字表上；`null` = 老后端没给（标题不编表名）。 */
  listType: HanziListType | null;
  /** 原文行号（1 开始）。 */
  line: number;
  /** 这一行的条目，**按 `indexInLine` 升序**。 */
  positions: T[];
  /** 分组标题（逐字）：`第 3 行（7 个字）` / 混表时 `识字表 · 第 3 行（7 个字）`。 */
  title: string;
}

/** 导入预览的分组（`positions` 是预览里的位置明细）。 */
export type ImportPreviewLineGroup = LineGroup<ListingPositionInfo>;

/**
 * 分组标题。
 *
 * @param input.showListType 是否带表名（只有这次预览里**混了两张表**时才带：
 *        单表时表名在预览顶上已经有了，每一行再重复一遍只是噪音）。
 * @param input.unit 行标题里的量词（默认 `个字`）。**词语表传 `个词`** ——
 *        一个词不是一个字，标题写成「第 3 行（7 个字）」会让人以为这里列的是字。
 */
export function importLineGroupTitle(input: {
  line: number;
  count: number;
  listType: HanziListType | null;
  showListType: boolean;
  unit?: string;
}): string {
  const linePart = `第 ${input.line} 行（${input.count} ${input.unit ?? '个字'}）`;
  if (!input.showListType || input.listType === null) return linePart;
  return `${listTypeShortLabel(input.listType)} · ${linePart}`;
}

/** `groupByLine()` 的可选参数（量词只有词语表用得上）。 */
export interface LineGroupOptions {
  /**
   * 行标题里的量词：`个字`（默认，字表）/ `个词`（词语表）。
   *
   * ⚠️ 只影响**标题文案**，分组口径（一行 = 一块、行号升序、组内按
   *    `indexInLine`）两边完全一样 —— 所以不另抄一份分组实现。
   */
  unit?: string;
}

/**
 * 把任意 `{line, indexInLine, listType?}` 分成「行」。
 *
 * ⚠️ **不改入参**（先把对象收进新数组再排序）：`positions` 是 `report` 里的数据，
 *    排序原地改它会让"重新预览"前后的引用悄悄变味。
 */
export function groupByLine<T extends LineGroupedItem>(
  items: readonly T[],
  options: LineGroupOptions = {},
): LineGroup<T>[] {
  const groups = new Map<string, LineGroup<T>>();
  for (const position of items) {
    const listType = position.listType ?? null;
    const key = `${listType ?? ''}|${position.line}`;
    const existing = groups.get(key);
    if (existing !== undefined) {
      existing.positions.push(position);
      continue;
    }
    groups.set(key, {
      key,
      listType,
      line: position.line,
      positions: [position],
      /** 计数要等收集完才知道，先占位（下面统一填）。 */
      title: '',
    });
  }

  const ordered = [...groups.values()];
  /**
   * 行号升序。`Array.prototype.sort` 是**稳定**的：同一行号出现在两张表上时，
   * 保持它们在 `items` 里第一次出现的先后（= 粘贴顺序）。
   */
  ordered.sort((left, right) => left.line - right.line);

  /** 混了两张表 ⇒ 标题必须带表名，否则两个「第 3 行」分不清。 */
  const distinctListTypes = new Set(ordered.map((group) => group.listType ?? ''));
  const showListType = distinctListTypes.size > 1;

  for (const group of ordered) {
    /** 组内按 `indexInLine` 升序（= `sort_order`）；稳定排序保留同序号的粘贴先后。 */
    group.positions.sort((left, right) => left.indexInLine - right.indexInLine);
    group.title = importLineGroupTitle({
      line: group.line,
      count: group.positions.length,
      listType: group.listType,
      showListType,
      ...(options.unit !== undefined ? { unit: options.unit } : {}),
    });
  }

  return ordered;
}

/**
 * 导入预览专用：`readings.positions` 分成行。
 *
 * ⚠️ 只是 `groupByLine()` 的一个**有类型的别名**（保留旧调用点与旧单测的名字）。
 */
export function groupImportPositionsByLine(
  positions: readonly ListingPositionInfo[],
): ImportPreviewLineGroup[] {
  return groupByLine(positions);
}

/**
 * 预览标题里那句"共 N 个字、M 行"（行数 = 分组数）。
 *
 * ⚠️ 2026-10-21：原来写的是「N 个**位置**」—— 界面上别处都说"字"（「生字 N 条」、
 *    「第 3 行（7 个字）」），只有这里冒出一个"位置"的说法，读起来像另一个数。
 *    这些位置本来就都是**字**（词语表不走这个预览），所以统一说"个字"。
 */
export function describeImportPreviewCounts(
  positions: readonly ListingPositionInfo[],
  lineCount: number,
): string {
  return `共 ${positions.length} 个字、${lineCount} 行，按你粘贴的行分组`;
}
